// Loyalty banner — appears in the cart panel once the customer has
// entered an email. Renders a single line for the existing guest
// (balance + tier + next-tier nudge) or a softer "join automatically"
// line for first-time guests when the programme has auto_enrol.
//
// Hidden entirely if the tenant doesn't have an enabled programme,
// or if the email is too short to query yet.
//
// `onSummary` lets the parent reuse the same lookup for downstream
// features (e.g. the points-redemption control near the cart total).

import { useEffect, useState } from "react";
import { fetchGuestLoyaltyByToken, type LoyaltySummary } from "../state/loyalty";
import { useGuest } from "../state/guest";

export function LoyaltyBanner({
  email: _email,
  onSummary,
}: {
  /** Unused since the capability rework — the token carries the tenant. */
  tenantId?: string;
  /** Unused since the OTP rework — kept so call sites don't churn. */
  email?: string;
  onSummary?: (summary: LoyaltySummary | null) => void;
}) {
  const { token } = useGuest();
  const [summary, setSummary] = useState<LoyaltySummary | null>(null);

  // The typed-email lookup this banner used to debounce lost its anon grant
  // in 20260802000030 — balances were readable (and spendable) by anyone who
  // knew an email address, so every request here has 401'd since. It now runs
  // only for a guest holding a CAPABILITY TOKEN, and the server reads the
  // email off that token rather than off a parameter, so nobody can ask for a
  // balance that is not theirs. Fires once per sign-in, not per keystroke.
  useEffect(() => {
    if (!token) {
      setSummary(null);
      onSummary?.(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await fetchGuestLoyaltyByToken(token);
      if (cancelled) return;
      if (result.ok && result.programme_active) {
        setSummary(result);
        onSummary?.(result);
      } else {
        setSummary(null);
        onSummary?.(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, onSummary]);

  if (!summary) return null;
  if (!summary.programme_active) return null;

  // Brand-new guest who hasn't booked before — show join nudge.
  if (!summary.enrolled) {
    if (!summary.auto_enrol) return null;
    return (
      <div className="vt-loyalty-banner vt-loyalty-banner-new">
        <strong>{summary.programme_name ?? "Rewards"}.</strong>{" "}
        You'll earn points on this booking — your account is created automatically at checkout.
      </div>
    );
  }

  const balance = summary.current_points_balance ?? 0;
  const tierName = summary.current_tier?.name;
  const nextTier = summary.next_tier;

  return (
    <div className="vt-loyalty-banner">
      <div className="vt-loyalty-banner-top">
        <span className="vt-loyalty-balance">
          {balance.toLocaleString()} points
        </span>
        {tierName && (
          <span className="vt-loyalty-tier" aria-label={`Current tier: ${tierName}`}>
            {tierName}
          </span>
        )}
      </div>
      {nextTier && nextTier.points_to_unlock > 0 && (
        <div className="vt-loyalty-next">
          {nextTier.points_to_unlock.toLocaleString()} more to unlock{" "}
          <strong>{nextTier.name}</strong>
        </div>
      )}
    </div>
  );
}
